/**
 * directoryMapApi: the resource directory's map read.
 *
 *   GET /api/t/{slug}/v1/providers/map
 *     -> { pins, not_placed, no_address_count,
 *          individual_pins, individuals_not_placed, individuals_no_address_count }
 *
 * The directory floor (auth-only, the same as GET /providers/all): every
 * internal seat that can open the directory can open its map. The
 * directory's own filters run in the browser over GET /providers/all's
 * and GET /individuals/all's rows; this read only says where each
 * organization and each org-less person sits, and which people each
 * organization's pin should name, so the map view joins the three by id
 * (DirectoryMapView).
 */

import { useQuery } from '@tanstack/react-query';
import { ApiError } from './api';
import type { ProviderKind } from './types';

export type ProviderMapPrecision = 'street' | 'zip' | 'none';

/** One person an organization's pin card lists: the people we know there. */
export interface ProviderMapPersonWire {
  id: string;
  full_name: string;
  /** The affiliation's own title when it has one, else the person's. */
  title: string | null;
  role: string | null;
  is_recommended: boolean;
  /** business_phone, else cell_phone. */
  phone: string | null;
}

export interface ProviderMapPinWire {
  id: string;
  name: string;
  /** provider_categories.slug, the directory's own category key. */
  category: string;
  category_label: string;
  kind: ProviderKind;
  lat: number;
  lng: number;
  precision: ProviderMapPrecision;
  is_recommended: boolean;
  recommended_at: string | null;
  phone: string | null;
  address: string;
  /** Set (true) for an organization entered as a town and a state with
   *  no street: its pin is its town, by design. */
  town_only?: boolean;
  /** Active individuals affiliated with this organization, by name. */
  people: ProviderMapPersonWire[];
}

export type ProviderMapNotPlacedReason =
  | 'no_address'
  | 'not_checked'
  | 'not_found'
  | 'not_found_add_state'
  | 'no_street_address';

export interface ProviderMapNotPlacedWire {
  id: string;
  name: string;
  reason: ProviderMapNotPlacedReason;
}

/** A pin of a person's own: active, no active organization link, placed. */
export interface IndividualMapPinWire {
  id: string;
  full_name: string;
  title: string | null;
  specialties: string | null;
  lat: number;
  lng: number;
  precision: ProviderMapPrecision;
  is_recommended: boolean;
  recommended_at: string | null;
  /** business_phone, else cell_phone. */
  phone: string | null;
  address: string;
  /** Set (true) for a person entered as a town and a state with no
   *  street. */
  town_only?: boolean;
}

export interface IndividualMapNotPlacedWire {
  id: string;
  full_name: string;
  reason: ProviderMapNotPlacedReason;
}

export interface ProviderMapWire {
  pins: ProviderMapPinWire[];
  not_placed: ProviderMapNotPlacedWire[];
  no_address_count: number;
  individual_pins: IndividualMapPinWire[];
  individuals_not_placed: IndividualMapNotPlacedWire[];
  individuals_no_address_count: number;
  request_id?: string;
}

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function fetchProviderMap(slug: string, signal?: AbortSignal): Promise<ProviderMapWire> {
  const url = `/api/t/${encodeURIComponent(slug)}/v1/providers/map`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<ProviderMapWire>(res, `GET ${url}`);
}

export function providerMapKey(slug: string): readonly unknown[] {
  return ['provider-map', slug] as const;
}

export function useProviderMap(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<ProviderMapWire>({
    queryKey: providerMapKey(slug),
    queryFn: ({ signal }) => fetchProviderMap(slug, signal),
    staleTime: 60_000,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
